import { Fragment } from 'react';

import { useOrdersApi } from './api/useOrdersApi';
import { Col } from './Col';
import { appConfig } from './config';
import { EmptyLink } from './EmptyLink';
import { RestartPaymentForm } from './form/RestartPaymentForm';
import { Div, P } from './HTMLElement';
import { t } from './i18n/i18n';
import { ImageSwitcher } from './ImageSwitcher';
import { InvoiceSummary } from './InvoiceSummary';
import { OrderDetailsProps } from './OrderDetails';
import { Link } from './router/Link';
import { Row } from './Row';
import { Table } from './Table';
import { TableBody } from './TableBody';
import { TableFoot } from './TableFoot';
import { TableHead } from './TableHead';
import { downloadFileFromUrl, formatCurrency, renderUI } from './util';

export const OrderDetailsA = ({ order, rightOfWithdrawalPath }: OrderDetailsProps) => {
  const { getInvoiceFile } = useOrdersApi();

  const rightOfWithdrawalInDate = order.withdrawalEndDate ? new Date(order.withdrawalEndDate) > new Date() : false;

  return (
    <div className="order-details-a">
      {order.warehouseOrders?.map((warehouseOrder) =>
        renderUI({
          bs5: (
            <Fragment key={warehouseOrder.id}>
              <Table borderless className="test small m-0 d-none d-md-table border-bottom" colNumber={12}>
                <TableHead>
                  <tr>
                    <th className="text-uppercase" colSpan={8}>
                      <b>{t('account.orders.details.product')}</b>
                    </th>
                    <th className="text-uppercase" colSpan={2}>
                      <b>{t('account.orders.details.price')}</b>
                    </th>
                    <th className="text-uppercase text-end" colSpan={2}>
                      <b>{t('account.orders.details.total')}</b>
                    </th>
                  </tr>
                </TableHead>
                <TableBody>
                  {warehouseOrder.orderedSkus?.map((orderSku) => {
                    return (
                      <tr key={`${warehouseOrder.id}-${orderSku.id || orderSku.sku?.id}`}>
                        <td colSpan={8}>
                          <div className="d-flex">
                            <div className="col-product-img" style={{ maxWidth: 144 }}>
                              {orderSku.sku?.product?.image && (
                                <ImageSwitcher
                                  aspectRatio={appConfig.productImageAspectRatio}
                                  fluid
                                  format={144}
                                  src={orderSku.sku.product.image.src}
                                />
                              )}
                            </div>
                            <div className="col-product-detail ps-3">
                              <p className="m-0">{orderSku.sku?.product?.name}</p>
                              <p className="text-dark m-0">
                                {orderSku.sku?.product?.attributes?.map(
                                  (attribute) =>
                                    attribute.typeCode === 'EQS_COLOR' && (
                                      <span key={attribute.typeCode}>
                                        {t('account.orders.details.product')} {attribute.value}
                                      </span>
                                    ),
                                )}
                              </p>
                              {orderSku.sku?.product?.name !== orderSku.sku?.name && (
                                <p className="text-dark mb-2">{orderSku.sku?.name}</p>
                              )}
                              {/*                                 <br />
                                {t('account.orders.details.size')} {orderSku.sku?.name}
 */}
                              <p className="text-dark m-0">
                                {t('account.orders.details.quantity')} {orderSku.quantity}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td
                          className="mobile-data-title"
                          colSpan={2}
                          data-title={t('account.orders.details.unitPrice')}
                        >
                          {appConfig.enablePricesWithExplicitTaxes
                            ? formatCurrency(orderSku.priceTaxable)
                            : formatCurrency(orderSku.price)}
                        </td>
                        <td
                          className="mobile-data-title text-end"
                          colSpan={2}
                          data-title={t('account.orders.details.total')}
                        >
                          {/* TODO Far arrivare già il totale dalle API */}
                          <b>
                            {appConfig.enablePricesWithExplicitTaxes
                              ? formatCurrency(orderSku.priceTaxable * orderSku.quantity)
                              : formatCurrency(orderSku.price * orderSku.quantity)}
                          </b>
                        </td>
                      </tr>
                    );
                  })}
                </TableBody>
                <TableFoot>
                  <tr>
                    <td colSpan={12}>
                      <Row gap={0}>
                        <Col size={12}>
                          <b>
                            {t('account.orders.details.warehouseName', {
                              warehouse: warehouseOrder.warehouseDisplayName,
                            })}
                          </b>
                        </Col>
                        <Col size={12}>{warehouseOrder.shippingProfileDescription}</Col>
                        {warehouseOrder.carrierTracking?.carrierTrackingUrl && (
                          <Col size={12}>
                            <Link
                              className="order-tracking-url"
                              target={'_blank'}
                              to={warehouseOrder.carrierTracking.carrierTrackingUrl}
                            >
                              {t('account.orders.details.trackingCode')}
                            </Link>
                          </Col>
                        )}
                        {appConfig.enableRightOfWithdrawal && (
                          <>
                            {rightOfWithdrawalPath && rightOfWithdrawalInDate && (
                              <Col size={12}>
                                <Div paddingBottom={2} paddingTop={3}>
                                  <Link className="small" to={rightOfWithdrawalPath}>
                                    {t('account.orders.details.rightOfWithdrawal')}
                                  </Link>
                                </Div>
                              </Col>
                            )}
                            {order.state === 'ARRIVED' && !rightOfWithdrawalInDate && (
                              <Col size={12}>
                                <Div paddingBottom={2} paddingTop={3}>
                                  <P className="small" margin={0}>
                                    <strong>
                                      <u>{t('account.orders.details.rightOfWithdrawalHasExpired')}</u>
                                    </strong>
                                  </P>
                                </Div>
                              </Col>
                            )}
                          </>
                        )}
                      </Row>
                    </td>
                  </tr>
                </TableFoot>
              </Table>

              <Div display={{ md: 'none' }}>
                {warehouseOrder.orderedSkus?.map((orderSku) => (
                  <Div key={`${warehouseOrder.id}-${orderSku.id || orderSku.sku?.id}`} marginTop={3}>
                    <Row gap={3}>
                      <Col size={4}>
                        {orderSku.sku?.product?.image && (
                          <ImageSwitcher fluid format={144} src={orderSku.sku.product.image.src} />
                        )}
                      </Col>
                      <Col size={8}>
                        <P className="small" fontWeight={700} margin={0}>
                          {orderSku.sku?.product?.name}
                        </P>
                        <P className="small text-dark" margin={0}>
                          {orderSku.sku?.product?.attributes?.map(
                            (attribute) =>
                              attribute.typeCode === 'EQS_COLOR' && (
                                <span key={attribute.typeCode}>
                                  {t('account.orders.details.product')} {attribute.value}
                                </span>
                              ),
                          )}
                        </P>
                        {orderSku.sku?.product?.name !== orderSku.sku?.name && (
                          <P className="small text-dark" margin={0}>
                            {t('account.orders.details.size')} {orderSku.sku?.name}
                          </P>
                        )}
                        <P className="small text-dark" margin={0}>
                          {t('account.orders.details.quantity')} {orderSku.quantity}
                        </P>
                      </Col>
                    </Row>
                    <P fontWeight={700} margin={0} marginTop={1} textAlign={'end'}>
                      {formatCurrency(
                        appConfig.enablePricesWithExplicitTaxes ? orderSku.price : orderSku.price * orderSku.quantity,
                      )}
                    </P>
                  </Div>
                ))}
                <Div marginY={3} paddingBottom={3} uiClassName={{ bs5: 'small border-bottom' }}>
                  <P margin={0}>
                    <b>
                      {t('account.orders.details.warehouseName', {
                        warehouse: warehouseOrder.warehouseDisplayName,
                      })}
                    </b>
                  </P>

                  <P margin={0}>{warehouseOrder.shippingProfileDescription}</P>
                  {warehouseOrder.carrierTracking?.carrierTrackingUrl && (
                    <P margin={0}>
                      <Link
                        className="order-tracking-url"
                        target={'_blank'}
                        to={warehouseOrder.carrierTracking.carrierTrackingUrl}
                      >
                        {t('account.orders.details.trackingCode')}
                      </Link>
                    </P>
                  )}
                  {appConfig.enableRightOfWithdrawal && (
                    <>
                      {rightOfWithdrawalPath && rightOfWithdrawalInDate && (
                        <P marginBottom={2} paddingTop={3}>
                          <Link className="small" to={rightOfWithdrawalPath}>
                            {t('account.orders.details.rightOfWithdrawal')}
                          </Link>
                        </P>
                      )}
                      {order.state === 'ARRIVED' && !rightOfWithdrawalInDate && (
                        <P marginBottom={2} paddingTop={3}>
                          <strong>
                            <u>{t('account.orders.details.rightOfWithdrawalHasExpired')}</u>
                          </strong>
                        </P>
                      )}
                    </>
                  )}
                </Div>
              </Div>
            </Fragment>
          ),
        }),
      )}
      <Div padding={{ md: 2, xs: 0 }} uiClassName={{ bs5: 'small' }}>
        <Row gapY={3}>
          <Col size={{ md: 4, xs: 12 }}>
            <P marginBottom={{ md: 3, xs: 0 }} uiClassName={{ bs5: 'fw-semi-bold' }}>
              {t('account.orders.details.shippingAddress')}
            </P>
            <P margin={0}>
              {order.shippingAddress?.name} {order.shippingAddress?.surname},
              <br />
              {order.shippingAddress?.addressLine1} {order.shippingAddress?.civicNumber}
              <br />
              {order.shippingAddress?.zipCode} {order.shippingAddress?.city}
            </P>
          </Col>
          <Col size={{ md: 4, xs: 12 }}>
            <P marginBottom={{ md: 3, xs: 0 }} uiClassName={{ bs5: 'fw-semi-bold' }}>
              {t('account.orders.details.paymentAddress')}
            </P>
            <P>
              {order.billingAddress?.name} {order.billingAddress?.surname},
              <br />
              {order.billingAddress?.addressLine1} {order.billingAddress?.civicNumber}
              <br />
              {order.billingAddress?.zipCode} {order.billingAddress?.city}
            </P>
            <P>{order.paymentMethod?.name}</P>
            <div>
              {order.invoice && order.billingAddress ? (
                <div>
                  <div>{t('checkout.invoice.requested')}</div>
                  <Div marginTop={2}>
                    <InvoiceSummary address={order.billingAddress} />
                    <div>
                      {order.invoiceFileReady && (
                        <EmptyLink
                          className="order-invoice-download"
                          // eslint-disable-next-line react/jsx-no-bind
                          onClick={() => {
                            getInvoiceFile(order.code).then((response) => {
                              downloadFileFromUrl(response.data.src, `${order.code}.pdf`);
                              // downloadFileFromBlob(response.data, `${order.code}.pdf`, 'application/pdf');
                            });
                          }}
                        >
                          {t('account.orders.details.invoiceDownload')}
                        </EmptyLink>
                      )}
                    </div>
                  </Div>
                </div>
              ) : (
                <div>{t('checkout.invoice.notRequested')}</div>
              )}
            </div>
          </Col>
          <Col size={{ md: 4, xs: 12 }}>
            <P marginBottom={{ md: 3, xs: 0 }} uiClassName={{ bs5: 'fw-semi-bold' }}>
              {t('account.orders.details.review')}
            </P>
            {appConfig.enablePricesWithExplicitTaxes ? (
              <>
                <Row justifyContent={'spaceBetween'}>
                  <Col size={'auto'}>{t('account.orders.details.totalProductsTaxable')}</Col>
                  <Col size={'auto'}>{formatCurrency(order.productsCostTaxable)}</Col>
                </Row>
                <Row justifyContent={'spaceBetween'}>
                  <Col size={'auto'}>{t('account.orders.details.tax')}</Col>
                  <Col size={'auto'}>{formatCurrency(order.productsTax)}</Col>
                </Row>
              </>
            ) : (
              <Row justifyContent={'spaceBetween'}>
                <Col size={'auto'}>{t('account.orders.details.totalProducts')}</Col>
                <Col size={'auto'}>{formatCurrency(order.productsCost)}</Col>
              </Row>
            )}
            <Row justifyContent={'spaceBetween'}>
              <Col size={'auto'}>{t('account.orders.details.totalShipping')}</Col>
              <Col size={'auto'}>{formatCurrency(order.shippingCost)}</Col>
            </Row>
            <Row justifyContent={'spaceBetween'}>
              <Col size={'auto'} uiClassName={{ bs5: 'fw-semi-bold' }}>
                {t('account.orders.details.totalAmount')}
              </Col>
              <Col size={'auto'} uiClassName={{ bs5: 'fw-semi-bold' }}>
                {formatCurrency(order.totalCost)}
              </Col>
            </Row>
            {!order.paid && !order.unrepayable && (
              <Div marginTop={3}>
                <RestartPaymentForm order={order} />
              </Div>
            )}
          </Col>
        </Row>
      </Div>
    </div>
  );
};
